import { useEffect } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { $getRoot, $insertNodes, ParagraphNode, TextNode } from 'lexical';
import { ListItemNode, ListNode } from '@lexical/list';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { CodeNode } from '@lexical/code';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

import { ExtendedTextNode } from './lexical-text-color/ExtendedTextNode';
import { TemplateVariableNode } from './lexical-template-variables/TemplateVariableNode';
import { UpdateTemplateVariablesPlugin } from './lexical-template-variables/UpdateTemplateVariablesPlugin';
import { ImageNode } from './lexical-image/ImageNode';
import { ImagePlugin } from './lexical-image/ImagePlugin';
import { ImagePastePlugin } from './lexical-image/ImagePastePlugin';
import { useInlineImageUpload } from './lexical-image/useInlineImageUpload';
import { ExtendedParagraphNode } from './lexical-paragraph-spacing/ExtendedParagraphNode';
import NoteEditorTheme from '../memory-bank/lexical/NoteEditorTheme';
import { ToolbarPlugin } from '../memory-bank/lexical/plugins/ToolbarPlugin';
import { ListMaxIndentLevelPlugin } from '../memory-bank/lexical/plugins/ListMaxIndentLevelPlugin';
import { AutoLinkPlugin } from '../memory-bank/lexical/plugins/AutoLinkPlugin';
import { KeyboardShortcutsPlugin } from '../memory-bank/lexical/plugins/KeyboardShortcutsPlugin';
import '../memory-bank/lexical/lexical-editor.css';

export interface HtmlWYSIWYGEditorProps {
  initialHtml?: string;
  onChange: (html: string) => void;
  placeholder?: string;
  height?: number;
  showToolbar?: boolean;
  /** Whether the formatting toolbar renders above or below the body. */
  toolbarPosition?: 'top' | 'bottom';
  /**
   * When false, the body is locked: the content area is no longer typeable and
   * the formatting toolbar is hidden. Defaults to true. Use this for read-only
   * views (e.g. viewing a published template) so the editor doesn't deceptively
   * appear editable when edits wouldn't be saved.
   */
  editable?: boolean;
  templateVariables?: Record<string, string>;
  /**
   * Overrides the image uploader. Receives the picked file, uploads it, and
   * returns the rendered `src` plus the durable `documentId`. Images always
   * round-trip via `<img data-document-id>` regardless; this only controls how
   * new ones are uploaded. When omitted, a default org-scoped uploader is used
   * (see {@link useInlineImageUpload}); pass `enableImageUpload={false}` to
   * disable image insertion entirely.
   */
  uploadImage?: (file: File) => Promise<{ src: string; documentId: string }>;
  /**
   * Whether images can be inserted (toolbar button + paste/drop). Defaults to
   * true, wiring the default org-scoped uploader when `uploadImage` is omitted.
   */
  enableImageUpload?: boolean;
}

function Placeholder({ placeholder }: { placeholder: string }) {
  return (
    <div className="absolute top-3 left-3 text-gray-400 pointer-events-none select-none">
      {placeholder}
    </div>
  );
}

const NODES = [
  HeadingNode,
  ListNode,
  ListItemNode,
  QuoteNode,
  TableNode,
  TableCellNode,
  TableRowNode,
  AutoLinkNode,
  LinkNode,
  CodeNode,
  TemplateVariableNode,
  ImageNode,
  // Preserve inline text color on HTML import (Lexical's default TextNode drops
  // it). Replaces TextNode everywhere in this editor.
  ExtendedTextNode,
  {
    replace: TextNode,
    with: (node: TextNode) => new ExtendedTextNode(node.__text),
    withKlass: ExtendedTextNode,
  },
  // Preserve block spacing (line-height) on HTML import — Lexical's default
  // ParagraphNode drops it. Replaces ParagraphNode everywhere in this editor.
  ExtendedParagraphNode,
  {
    replace: ParagraphNode,
    with: () => new ExtendedParagraphNode(),
    withKlass: ExtendedParagraphNode,
  },
];

/** Plugin that fires onChange with the editor's HTML content. */
function OnChangeHtml({ onChange }: { onChange: (html: string) => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        onChange(html);
      });
    });
  }, [editor, onChange]);

  return null;
}

/** Plugin that syncs the editor's editable state with the `editable` prop. */
function EditablePlugin({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);

  return null;
}

/** Plugin that loads initial HTML into the editor on mount. */
function InitHtmlPlugin({ html }: { html: string }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!html) return;
    editor.update(() => {
      const parser = new DOMParser();
      const dom = parser.parseFromString(html, 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      const root = $getRoot();
      root.clear();
      $insertNodes(nodes);
    });
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export function HtmlWYSIWYGEditor({
  initialHtml = '',
  onChange,
  placeholder = 'Write here...',
  height = 200,
  showToolbar = true,
  toolbarPosition = 'top',
  editable = true,
  templateVariables,
  uploadImage,
  enableImageUpload = true,
}: HtmlWYSIWYGEditorProps) {
  // Default to the org-scoped inline-image uploader so every email editor gets
  // the image button + paste/drop without per-consumer wiring. An explicit
  // `uploadImage` overrides it; `enableImageUpload={false}` disables images.
  const defaultUploadImage = useInlineImageUpload();
  const effectiveUploadImage = enableImageUpload
    ? (uploadImage ?? defaultUploadImage)
    : undefined;

  // Hide the formatting toolbar when locked — its buttons would be inert on a
  // non-editable editor and only signal "you can edit this" misleadingly.
  const toolbarVisible = showToolbar && editable;
  return (
    <LexicalComposer
      initialConfig={{
        namespace: 'html-wysiwyg-editor',
        theme: NoteEditorTheme,
        editable,
        onError(error: Error) {
          console.error('Lexical editor error:', error);
        },
        nodes: NODES,
      }}
    >
      <div className="border border-gray-300 rounded-md overflow-hidden bg-white">
        {toolbarVisible && toolbarPosition === 'top' && (
          <ToolbarPlugin uploadImage={effectiveUploadImage} />
        )}
        <div className="relative" style={{ minHeight: height }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="lexical-editor outline-none resize-none p-3 text-sm leading-relaxed"
                style={{ minHeight: height }}
              />
            }
            placeholder={<Placeholder placeholder={placeholder} />}
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        {toolbarVisible && toolbarPosition === 'bottom' && (
          <ToolbarPlugin uploadImage={effectiveUploadImage} />
        )}
      </div>

      <EditablePlugin editable={editable} />
      <InitHtmlPlugin html={initialHtml} />
      <OnChangeHtml onChange={onChange} />
      <UpdateTemplateVariablesPlugin templateVariables={templateVariables} />
      <ImagePlugin />
      {effectiveUploadImage && (
        <ImagePastePlugin uploadImage={effectiveUploadImage} />
      )}
      <HistoryPlugin />
      <ListPlugin />
      <LinkPlugin />
      <AutoLinkPlugin />
      <ListMaxIndentLevelPlugin maxDepth={7} />
      <KeyboardShortcutsPlugin />
    </LexicalComposer>
  );
}
